import { useCallback, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useClerk, useOrganization } from "@clerk/react"
import useLoggedInUser from "../../hooks/use-logged-in-user"
import { useCanManageEnterpriseSso } from "../../hooks/use-can-manage-enterprise-sso"
import { usePermissions } from "../../hooks/use-permissions"
import { ClerkOrgPublicMetadata } from "../../types/clerk-metadata"
import { createCrossAppToken } from "../../api/create-cross-app-token"
import { cn } from "../../lib/utils"
import { growLandingPath } from "../../lib/learnworlds"

import {
  LayoutDashboard,
  AlertTriangle,
  ClipboardCheck,
  AlertCircle,
  Settings,
  Users,
  Building2,
  Network,
  User,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ExternalLink,
  Sparkles,
  FileText,
  FileStack,
  ShieldCheck,
} from "lucide-react"

import plenumLogo from "../../assets/plenum-logo.svg"
import refinedLogo from "../../assets/logos/refined.svg?url"
import askLogo from "../../assets/logos/ask.svg?url"
import growLogo from "../../assets/logos/grow.svg?url"
import qualifiedLogo from "../../assets/logos/qualified.svg?url"

interface NavItemProps {
  icon: React.ReactNode
  label: string
  href?: string
  onClick?: () => void
  isActive?: boolean
  isExternal?: boolean
  accentColor?: string
}

const NavItem = ({
  icon,
  label,
  href,
  onClick,
  isActive,
  isExternal,
  accentColor,
}: NavItemProps) => {
  const navigate = useNavigate()

  const handleClick = () => {
    if (onClick) {
      onClick()
    } else if (href) {
      navigate(href)
    }
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
        "hover:bg-white/[0.08] active:scale-[0.98]",
        isActive ? "bg-white/[0.12] text-white" : "text-slate-400 hover:text-white",
      )}
    >
      {isActive && (
        <span
          className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-indigo-400 to-violet-500 shadow-lg shadow-indigo-500/30"
          style={{
            background: accentColor
              ? `linear-gradient(to bottom, ${accentColor}, ${accentColor})`
              : undefined,
          }}
        />
      )}
      <span
        className={cn(
          "flex-shrink-0 transition-colors duration-200",
          isActive ? "text-white" : "text-slate-500 group-hover:text-slate-300",
        )}
      >
        {icon}
      </span>
      <span className="flex-1 text-left truncate">{label}</span>
      {isExternal && (
        <ExternalLink className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-60" />
      )}
    </button>
  )
}

interface NavSectionProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  accentColor?: string
}

const NavSection = ({
  title,
  icon,
  children,
  defaultOpen = true,
  accentColor,
}: NavSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group flex w-full items-center gap-2.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-300"
      >
        <span className="flex-shrink-0 opacity-60" style={{ color: accentColor }}>
          {icon}
        </span>
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            isOpen ? "rotate-0" : "-rotate-90",
          )}
        />
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-out",
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="space-y-0.5 pl-1">{children}</div>
      </div>
    </div>
  )
}

const ProductLink = ({
  logo,
  name,
  color,
  onClick,
}: {
  logo: string
  name: string
  color: string
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    className="group flex items-center gap-2.5 rounded-lg px-2.5 py-2 transition-all duration-200 hover:bg-white/[0.06] active:scale-[0.98]"
  >
    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/[0.08] transition-colors group-hover:bg-white/[0.12]">
      <img src={logo} alt="" className="h-4 w-4" />
    </div>
    <span className="text-sm font-medium transition-colors" style={{ color }}>
      {name}
    </span>
    <ExternalLink className="ml-auto h-3 w-3 opacity-0 transition-opacity group-hover:opacity-40" />
  </button>
)

const Sidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signOut } = useClerk()
  const { organization } = useOrganization()
  const loggedInUser = useLoggedInUser()
  const [isCollapsed, setIsCollapsed] = useState(false)

  const orgMetadata = organization?.publicMetadata as ClerkOrgPublicMetadata | undefined
  const growPath =
    orgMetadata?.grow?.active === true &&
    orgMetadata.grow.learnworldsSsoEnabled === true &&
    organization
      ? growLandingPath(organization.id)
      : null
  const growUrl = (() => {
    if (!orgMetadata?.grow?.active || growPath) return null
    const email = loggedInUser?.email || ""
    const emailDomain = email.split("@")[1]?.toLowerCase() || ""
    const candidateUrl =
      (emailDomain && orgMetadata.grow.domainUrls?.[emailDomain]) ||
      orgMetadata.grow.url ||
      "https://grow.plenumapps.com"
    try {
      const u = new URL(candidateUrl)
      const h = u.hostname.toLowerCase()
      if (u.protocol !== "https:") return null
      if (h !== "plenumapps.com" && !h.endsWith(".plenumapps.com")) return null
      return u.toString()
    } catch {
      return null
    }
  })()

  const isAdmin = loggedInUser?.isAdmin || false
  const canManageEnterpriseSso = useCanManageEnterpriseSso()
  const { can, roles } = usePermissions()
  const isSafetyAdmin = roles.some((role) => role.id === "safety-admin")
  const isActive = (path: string) => location.pathname.startsWith(path)

  const handleExternalLink = (url: string) => () => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  const handleQualifyLink = useCallback(async () => {
    try {
      const response = await createCrossAppToken.mutate<{ token: string }>({
        body: { targetApp: "clinked" },
      })
      if (response.status === 200 && response.data?.token) {
        const url = `https://qualified.plenumapps.com/uaa/login/jwt?token=${encodeURIComponent(response.data.token)}`
        window.open(url, "_blank", "noopener,noreferrer")
      } else {
        window.open("https://qualified.plenumapps.com/", "_blank", "noopener,noreferrer")
      }
    } catch {
      window.open("https://qualified.plenumapps.com/", "_blank", "noopener,noreferrer")
    }
  }, [])

  const handleLogOut = async () => {
    await signOut()
    navigate("/sign-in")
  }

  const userInitials =
    `${loggedInUser?.first_name?.[0] || ""}${loggedInUser?.last_name?.[0] || ""}`.toUpperCase() ||
    "?"

  return (
    <aside
      className={cn(
        "relative flex h-screen flex-col border-r border-white/[0.06] bg-[#0f1419] transition-all duration-300",
        isCollapsed ? "w-16" : "w-72",
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/20 via-transparent to-violet-950/10 pointer-events-none" />

      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-7 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-[#1a1f2e] text-slate-400 shadow-lg transition-all hover:bg-[#252b3d] hover:text-white"
      >
        <ChevronLeft
          className={cn("h-3.5 w-3.5 transition-transform", isCollapsed && "rotate-180")}
        />
      </button>

      <div
        className={cn(
          "relative flex items-center gap-3 border-b border-white/[0.06] p-5 transition-all",
          isCollapsed && "justify-center px-3",
        )}
      >
        <img src={plenumLogo} alt="Plenum" className="h-10 w-10 flex-shrink-0" />
        {!isCollapsed && (
          <div className="flex flex-col overflow-hidden">
            <span className="font-bold tracking-tight text-white">Plenum</span>
            <span className="text-xs text-slate-500 truncate">Vivid Platform</span>
          </div>
        )}
      </div>

      <div
        className={cn(
          "relative flex items-center gap-3 border-b border-white/[0.06] p-4 transition-all",
          isCollapsed && "justify-center px-2",
        )}
      >
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-slate-800 text-xs font-bold text-white ring-2 ring-white/10">
          {userInitials}
        </div>
        {!isCollapsed && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <span className="text-sm font-medium text-white truncate">
              {loggedInUser?.first_name} {loggedInUser?.last_name}
            </span>
            <span className="text-xs text-slate-500 truncate">{loggedInUser?.email}</span>
          </div>
        )}
      </div>

      <nav className="sidebar-scroll relative flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => navigate("/dashboard/lagging")}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg transition-all",
                isActive("/dashboard")
                  ? "bg-white/[0.12] text-white"
                  : "text-slate-500 hover:bg-white/[0.08] hover:text-white",
              )}
            >
              <LayoutDashboard className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/incidents/reports")}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg transition-all",
                isActive("/incidents")
                  ? "bg-white/[0.12] text-white"
                  : "text-slate-500 hover:bg-white/[0.08] hover:text-white",
              )}
            >
              <AlertTriangle className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/inspections/reports")}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg transition-all",
                isActive("/inspections")
                  ? "bg-white/[0.12] text-white"
                  : "text-slate-500 hover:bg-white/[0.08] hover:text-white",
              )}
            >
              <ClipboardCheck className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/issues/reports")}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg transition-all",
                isActive("/issues")
                  ? "bg-white/[0.12] text-white"
                  : "text-slate-500 hover:bg-white/[0.08] hover:text-white",
              )}
            >
              <AlertCircle className="h-5 w-5" />
            </button>
            {(canManageEnterpriseSso ||
              (isAdmin &&
                (can.admin.usersView ||
                  can.admin.organizationsView ||
                  can.admin.entitiesView))) && (
              <button
                onClick={() =>
                  navigate(
                    can.admin.usersView
                      ? "/admin/users"
                      : can.admin.organizationsView
                        ? "/admin/organizations"
                        : can.admin.entitiesView
                          ? "/admin/entity-assignments"
                          : "/admin/identity-and-sso",
                  )
                }
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-lg transition-all",
                  isActive("/admin")
                    ? "bg-white/[0.12] text-white"
                    : "text-slate-500 hover:bg-white/[0.08] hover:text-white",
                )}
              >
                <Settings className="h-5 w-5" />
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-1.5">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                Products
              </p>
              <div className="space-y-0.5">
                <ProductLink
                  logo={refinedLogo}
                  name="Refined"
                  color="oklch(70% 53% 137deg)"
                  onClick={handleExternalLink("https://refined.plenumapps.com")}
                />
                <ProductLink
                  logo={askLogo}
                  name="Ask"
                  color="oklch(75% 30% 202deg)"
                  onClick={handleExternalLink("https://ask.plenumapps.com")}
                />
                {(growPath || growUrl) && (
                  <ProductLink
                    logo={growLogo}
                    name="Grow"
                    color="oklch(77% 31% 246deg)"
                    onClick={growPath ? () => navigate(growPath) : handleExternalLink(growUrl!)}
                  />
                )}
                <ProductLink
                  logo={qualifiedLogo}
                  name="Qualify"
                  color="oklch(68% 46% 33deg)"
                  onClick={handleQualifyLink}
                />
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

            {!isSafetyAdmin && (
              <NavSection
                title="Dashboard"
                icon={<LayoutDashboard className="h-3.5 w-3.5" />}
                accentColor="#818cf8"
              >
                <NavItem
                  icon={<LayoutDashboard className="h-4 w-4" />}
                  label="Summary (Lagging)"
                  href="/dashboard/lagging"
                  isActive={isActive("/dashboard/lagging") || isActive("/dashboard/summary")}
                  accentColor="#818cf8"
                />
                <NavItem
                  icon={<Sparkles className="h-4 w-4" />}
                  label="Leading"
                  href="/dashboard/leading"
                  isActive={isActive("/dashboard/leading")}
                  accentColor="#818cf8"
                />
                <NavItem
                  icon={<Settings className="h-4 w-4" />}
                  label="Configuration"
                  href="/dashboard/configuration"
                  isActive={isActive("/dashboard/configuration")}
                  accentColor="#818cf8"
                />
              </NavSection>
            )}

            <NavSection
              title="Incidents"
              icon={<AlertTriangle className="h-3.5 w-3.5" />}
              accentColor="#f59e0b"
            >
              <NavItem
                icon={<FileText className="h-4 w-4" />}
                label="Reports"
                href="/incidents/reports"
                isActive={isActive("/incidents/reports")}
                accentColor="#f59e0b"
              />
              {can.incidents.formsView && (
                <NavItem
                  icon={<FileStack className="h-4 w-4" />}
                  label="Forms"
                  href="/incidents/forms"
                  isActive={isActive("/incidents/forms")}
                  accentColor="#f59e0b"
                />
              )}
            </NavSection>

            <NavSection
              title="Inspections"
              icon={<ClipboardCheck className="h-3.5 w-3.5" />}
              accentColor="#10b981"
            >
              <NavItem
                icon={<FileText className="h-4 w-4" />}
                label="Reports"
                href="/inspections/reports"
                isActive={isActive("/inspections/reports")}
                accentColor="#10b981"
              />
              {can.inspections.formsView && (
                <NavItem
                  icon={<FileStack className="h-4 w-4" />}
                  label="Forms"
                  href="/inspections/forms"
                  isActive={isActive("/inspections/forms")}
                  accentColor="#10b981"
                />
              )}
            </NavSection>

            <NavSection
              title="Issues"
              icon={<AlertCircle className="h-3.5 w-3.5" />}
              accentColor="#ef4444"
            >
              <NavItem
                icon={<FileText className="h-4 w-4" />}
                label="Reports"
                href="/issues/reports"
                isActive={isActive("/issues/reports")}
                accentColor="#ef4444"
              />
              {can.issues.formsView && (
                <NavItem
                  icon={<FileStack className="h-4 w-4" />}
                  label="Forms"
                  href="/issues/forms"
                  isActive={isActive("/issues/forms")}
                  accentColor="#ef4444"
                />
              )}
            </NavSection>

            {(isAdmin || canManageEnterpriseSso) && (
              <NavSection
                title="Admin"
                icon={<Settings className="h-3.5 w-3.5" />}
                accentColor="#a78bfa"
              >
                {can.admin.usersView && (
                  <NavItem
                    icon={<Users className="h-4 w-4" />}
                    label="User Management"
                    href="/admin/users"
                    isActive={isActive("/admin/users")}
                    accentColor="#a78bfa"
                  />
                )}
                {can.admin.organizationsView && (
                  <NavItem
                    icon={<Building2 className="h-4 w-4" />}
                    label="Organizations"
                    href="/admin/organizations"
                    isActive={isActive("/admin/organizations")}
                    accentColor="#a78bfa"
                  />
                )}
                {can.admin.entitiesView && (
                  <NavItem
                    icon={<Network className="h-4 w-4" />}
                    label="Entity Management"
                    href="/admin/entity-assignments"
                    isActive={isActive("/admin/entity-assignments")}
                    accentColor="#a78bfa"
                  />
                )}
                {canManageEnterpriseSso && (
                  <NavItem
                    icon={<ShieldCheck className="h-4 w-4" />}
                    label="Identity & SSO"
                    href="/admin/identity-and-sso"
                    isActive={isActive("/admin/identity-and-sso")}
                    accentColor="#a78bfa"
                  />
                )}
              </NavSection>
            )}
          </div>
        )}
      </nav>

      <div
        className={cn(
          "relative border-t border-white/[0.06] p-3 transition-all",
          isCollapsed && "px-2",
        )}
      >
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => navigate("/settings/profile")}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition-all hover:bg-white/[0.08] hover:text-white"
            >
              <User className="h-5 w-5" />
            </button>
            <button
              onClick={handleLogOut}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition-all hover:bg-red-500/20 hover:text-red-400"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="space-y-0.5">
            <NavItem
              icon={<User className="h-4 w-4" />}
              label="Profile"
              href="/settings/profile"
              isActive={isActive("/settings/profile")}
            />
            <NavItem
              icon={<HelpCircle className="h-4 w-4" />}
              label="Support"
              href="/settings/support"
              isActive={isActive("/settings/support")}
            />
            <button
              onClick={handleLogOut}
              className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 transition-all duration-200 hover:bg-red-500/10 hover:text-red-400 active:scale-[0.98]"
            >
              <LogOut className="h-4 w-4 flex-shrink-0 text-slate-500 transition-colors group-hover:text-red-400" />
              <span>Log Out</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

export default Sidebar
